import { LicenseTransferForm } from "@/components/license-transfer-form";
import PageHeader from "@/components/page-header";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { getLicenseDetailsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { validate as uuidValidate } from "uuid";
import { z } from "zod";

const transferSearchSchema = z.object({
  fromDeregistration: z.boolean().optional().catch(false),
});

export const Route = createFileRoute("/operator/licenses/$licenseId/transfer")({
  component: TransferLicenseScreen,
  validateSearch: transferSearchSchema,
});

function TransferLicenseScreen() {
  const { licenseId } = Route.useParams();
  const { fromDeregistration } = Route.useSearch();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const isValidLicenseId = uuidValidate(licenseId);
  const navigate = useNavigate();

  const {
    data,
    isLoading: isQueryLoading,
    error: queryError,
  } = useQuery({
    ...getLicenseDetailsOptions({
      path: {
        license_id: licenseId,
      },
    }),
    enabled: isAuthSetupComplete && isValidLicenseId,
  });

  const handleSuccess = (result?: { new_license_id?: string }) => {
    if (result?.new_license_id) {
      navigate({
        to: "/operator/registrations",
        search: { licenseId: result.new_license_id },
      });
      return;
    }
    navigate({
      to: "/operator/overview",
    });
  };

  if (!isValidLicenseId) {
    return (
      <ErrorComponent
        title="Invalid URL"
        message={`The license ID '${licenseId}' is not a valid license ID`}
      />
    );
  }
  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (isQueryLoading) {
    return <LoadingScreen message="Fetching license..." />;
  }
  if (queryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  }

  if (!data?.is_transferable) {
    return (
      <ErrorComponent
        title="License not transferable"
        message="License is not in a status that allows it to be transferred."
      />
    );
  }

  return (
    <div className="flex flex-col container mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Service Transfer"
        subtitle={
          fromDeregistration
            ? "Complete the service transfer requested during de-registration"
            : undefined
        }
      />
      <div>
        <LicenseTransferForm
          licenseId={licenseId}
          licenseTypeName={data.license_type.name}
          propertyAddress={data.property?.display_address}
          fromDeregistration={Boolean(fromDeregistration)}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
}
